import type { Content } from './schema.js';
import pl from './content/pl.json';
import en from './content/en.json';
import de from './content/de.json';

export const locales = ['pl', 'en', 'de'] as const;
export type Locale = (typeof locales)[number];

export const defaultLocale: Locale = 'pl';

const contentByLocale: Record<Locale, Content> = {
  pl: pl as Content,
  en: en as Content,
  de: de as Content,
};

export function loadContent(locale: Locale): Content {
  return contentByLocale[locale];
}

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (locales as readonly string[]).includes(value);
}

/** Map a locale to the URL prefix used for that locale's prerendered page. */
export function localeToPath(locale: Locale): string {
  return locale === defaultLocale ? '/' : `/${locale}/`;
}

/**
 * Build the locale-specific path for a given legal page. The default (Polish)
 * locale lives at the root (`/privacy`), non-default locales under their
 * language prefix (`/en/privacy`, `/de/privacy`).
 */
export function localeLegalPath(locale: Locale, page: 'privacy' | 'terms'): string {
  return locale === defaultLocale ? `/${page}` : `/${locale}/${page}`;
}

/** Convenience: path to the Privacy Policy page for a given locale. */
export function localePrivacyPath(locale: Locale): string {
  return localeLegalPath(locale, 'privacy');
}

/** Convenience: path to the Terms of Use page for a given locale. */
export function localeTermsPath(locale: Locale): string {
  return localeLegalPath(locale, 'terms');
}
